import { ui } from "@patient-watch/ui";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@patient-watch/ui/form";
import { Slider as ShadcnSlider } from "@patient-watch/ui/slider";
import type { InputT } from "@patient-watch/types";
import { useFormContext } from "react-hook-form";
import { resolveStep, roundToDecimals, stepDecimals } from "../../features/inputs/inputStep";
import { sliderEndAnchors, sliderTicks } from "../../features/inputs/sliderTicks";
import { cn } from "../../lib/utils";
import { parseSliderText, sliderCommittedValue, sliderRestValue } from "./sliderValue";

interface Props {
    input: InputT;
    label: string;
    value: number | null;
    setValue: (value: number | null) => void;
    /** Defaults to input.input_id when omitted (used for nested ms-adl ratings). */
    fieldName?: string;
}

const Slider = (props: Props) => {
    const minValue = props.input.min_value ?? 0;
    const maxValue = props.input.max_value ?? 100;
    const restValue = sliderRestValue(minValue, maxValue);
    // inputs.step sets the granularity (0.1, 0.101, 1, 5, 10 ...). Null keeps whole numbers.
    const step = resolveStep(props.input.step, 1);
    const decimals = stepDecimals(step);
    const { control, getValues } = useFormContext();
    const isVertical = props.input.orientation === "vertical";
    const fieldName = props.fieldName ?? props.input.input_id;
    const isRequired = !props.input.is_optional;

    // Get min/max labels from scale_options if available
    const minOption = props.input.scale_options?.find(
        (opt) => opt.value === minValue || opt.value === String(minValue)
    );
    const maxOption = props.input.scale_options?.find(
        (opt) => opt.value === maxValue || opt.value === String(maxValue)
    );
    const minLabel = minOption?.label;
    const maxLabel = maxOption?.label;
    // A label under every value (e.g. 0-10) when scale_options label each one; otherwise the end labels.
    // A label like "10 = Worst possible" shows its number, with the words under that end.
    const ticks = isVertical ? null : sliderTicks(props.input);
    const anchors = sliderEndAnchors(ticks);

    return (
        <FormField
            control={control}
            name={fieldName}
            render={({ field, fieldState }) => {
                // Unanswered stays blank. A midpoint such as 5 is a real score, not a placeholder.
                const committed = sliderCommittedValue(field.value);
                const numberText = committed == null ? "" : roundToDecimals(committed, decimals).toString();
                const hasError = !!fieldState.error;

                const handleValueChange = (newValue: number) => {
                    field.onChange(newValue);
                    props.setValue(newValue);
                };

                const handleNumberChange = (raw: string) => {
                    const parsed = parseSliderText(raw);
                    if (parsed === undefined) return;
                    if (parsed === null) {
                        field.onChange(null);
                        props.setValue(null);
                        return;
                    }
                    handleValueChange(parsed);
                };

                return (
                    <FormItem>
                        <ui.Card
                            className={cn(
                                "flex flex-col justify-center items-center gap-4 mx-auto px-0 w-full",
                                hasError && "border-rose-200 bg-rose-50/80 dark:border-rose-800/60 dark:bg-rose-950/25"
                            )}
                        >
                            <ui.CardHeader className="w-full">
                                <FormLabel className="w-full font-normal text-md text-slate-800 dark:text-slate-100 whitespace-pre-line">
                                    {props.input.input_caption ? (
                                        <span className="block mb-1 font-normal text-muted-foreground text-sm italic">
                                            {props.input.input_caption}
                                        </span>
                                    ) : null}
                                    {props.label}
                                    {isRequired ? <span className="ml-0.5 text-red-500">*</span> : null}
                                </FormLabel>
                                {props.input.input_description && (
                                    <FormDescription>{props.input.input_description}</FormDescription>
                                )}
                            </ui.CardHeader>
                            <ui.CardContent
                                className={cn(
                                    "flex mb-8 w-full",
                                    isVertical ? "flex-row items-stretch gap-6 justify-center" : "flex-col gap-y-8"
                                )}
                            >
                                {/* Vertical layout: number on left, slider with labels on right */}
                                {isVertical ? (
                                    <>
                                        {/* Number input on the left */}
                                        <div className="flex items-center">
                                            <FormControl>
                                                <ui.Input
                                                    data-testid="slider-number-input"
                                                    min={minValue}
                                                    max={maxValue}
                                                    onChange={(e) => handleNumberChange(e.target.value)}
                                                    required={isRequired}
                                                    aria-required={isRequired}
                                                    step={step}
                                                    value={numberText}
                                                    className="flex justify-center items-center aria-invalid:border-slate-300 dark:aria-invalid:border-slate-500 aria-invalid:ring-0 w-24 h-24 text-5xl text-center [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [appearance:textfield]"
                                                    type="number"
                                                />
                                            </FormControl>
                                        </div>
                                        {/* Slider with labels */}
                                        <div className="flex flex-col items-center gap-5">
                                            {/* Max label (top/best) */}
                                            {maxLabel && (
                                                <div className="flex flex-col items-center text-center">
                                                    <span className="font-semibold text-lg">{maxValue}</span>
                                                    <span className="max-w-[150px] text-muted-foreground text-xs">
                                                        {maxLabel}
                                                    </span>
                                                </div>
                                            )}
                                            {/* Vertical slider */}
                                            <div className="flex justify-center py-4 h-[280px]">
                                                <ShadcnSlider
                                                    min={minValue}
                                                    max={maxValue}
                                                    step={step}
                                                    value={[committed ?? restValue]}
                                                    onValueChange={([value]) =>
                                                        value !== undefined && handleValueChange(value)
                                                    }
                                                    aria-required={isRequired}
                                                    orientation="vertical"
                                                    className="h-full"
                                                    thumbClassName="border-4 border-indigo-500 w-10 h-10"
                                                />
                                            </div>
                                            {/* Min label (bottom/worst) */}
                                            {minLabel && (
                                                <div className="flex flex-col items-center text-center">
                                                    <span className="font-semibold text-lg">{minValue}</span>
                                                    <span className="max-w-[150px] text-muted-foreground text-xs">
                                                        {minLabel}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {/* Horizontal layout: number above slider */}
                                        <FormControl>
                                            <ui.Input
                                                data-testid="slider-number-input"
                                                min={minValue}
                                                max={maxValue}
                                                onChange={(e) => handleNumberChange(e.target.value)}
                                                required={isRequired}
                                                aria-required={isRequired}
                                                step={step}
                                                value={numberText}
                                                className={cn(
                                                    "flex justify-center items-center m-auto p-auto aria-invalid:border-slate-300 dark:aria-invalid:border-slate-500 aria-invalid:ring-0 w-16 h-16 text-4xl text-center [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [appearance:textfield]",
                                                    maxValue > 10 && "w-40"
                                                )}
                                                type="number"
                                            />
                                        </FormControl>
                                        <div className="flex flex-col gap-y-6">
                                            <ShadcnSlider
                                                min={minValue}
                                                max={maxValue}
                                                step={step}
                                                value={[committed ?? restValue]}
                                                onValueChange={([value]) =>
                                                    value !== undefined && handleValueChange(value)
                                                }
                                                // Unanswered, the thumb rests on restValue, and tapping that value is no
                                                // change to the slider; take it as the answer when the tap ends.
                                                onPointerUp={() => {
                                                    if (sliderCommittedValue(getValues(fieldName)) == null) {
                                                        handleValueChange(restValue);
                                                    }
                                                }}
                                                aria-required={isRequired}
                                                className="w-full"
                                                thumbClassName="border-4 border-indigo-500 w-12 h-12"
                                            />
                                            {/* A label under every value, only when there are no end words to show instead. */}
                                            {ticks && !anchors ? (
                                                <div
                                                    className="flex justify-between -mt-2 px-3 text-muted-foreground text-sm"
                                                    data-testid="slider-ticks"
                                                >
                                                    {ticks.map((tick) => (
                                                        <span
                                                            key={tick.value}
                                                            className={cn(
                                                                "w-6 text-center",
                                                                committed === tick.value &&
                                                                    "font-semibold text-foreground"
                                                            )}
                                                        >
                                                            {tick.label}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : null}
                                            {/* The end labels at the bottom, each as its number and its words ("0 = None"). */}
                                            {anchors && ticks ? (
                                                <div
                                                    className="flex justify-between -mt-2 text-muted-foreground text-sm"
                                                    data-testid="slider-anchors"
                                                >
                                                    <span className="max-w-[45%]">
                                                        {anchors.min ? `${ticks[0]?.label} = ${anchors.min}` : null}
                                                    </span>
                                                    <span className="max-w-[45%] text-right">
                                                        {anchors.max
                                                            ? `${ticks[ticks.length - 1]?.label} = ${anchors.max}`
                                                            : null}
                                                    </span>
                                                </div>
                                            ) : null}
                                            {/* The words for the value the slider is on, such as "1 = Very mild", when it has some. */}
                                            {ticks && anchors ? (
                                                <p
                                                    aria-live="polite"
                                                    data-testid="slider-value-caption"
                                                    className="-mt-4 min-h-6 font-medium text-center text-slate-800 dark:text-slate-100"
                                                >
                                                    {(() => {
                                                        const words = ticks.find(
                                                            (tick) => tick.value === committed
                                                        )?.anchor;
                                                        return committed != null && words
                                                            ? `${committed} = ${words}`
                                                            : "";
                                                    })()}
                                                </p>
                                            ) : null}
                                            {/* Min/Max labels from scale_options */}
                                            {!ticks && (minLabel || maxLabel) && (
                                                <div className="flex justify-between text-muted-foreground text-sm">
                                                    <span className="max-w-[45%]">
                                                        {minValue}
                                                        {minLabel && ` - ${minLabel}`}
                                                    </span>
                                                    <span className="max-w-[45%] text-right">
                                                        {maxValue}
                                                        {maxLabel && ` - ${maxLabel}`}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </ui.CardContent>
                            <ui.CardFooter>
                                <FormMessage />
                            </ui.CardFooter>
                        </ui.Card>
                    </FormItem>
                );
            }}
        />
    );
};

export default Slider;
